// frontend/src/components/charts/GraficoPizza.jsx (versão 0.2.1)

import React from 'react';
// Importações otimizadas - apenas o que é necessário
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import Card from '../UI/Card';
import CardContent from '../UI/CardContent';

// Cores padrão para os gráficos (ajustadas ao seu estilo)
const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F'];

/**
 * Componente de Gráfico de Rosca (Doughnut Chart)
 * @param {Array<Object>} data - Dados para o gráfico. Ex: [{ name: 'Disponível', value: 10 }]
 * @param {string} dataKey - Chave do valor no objeto de dados (ex: 'total', 'value')
 * @param {string} nameKey - Chave do nome/rótulo no objeto de dados (ex: 'status', 'tipo_tarefa')
 * @param {string} title - Título do gráfico
 * @param {string} subtitle - Subtítulo/descrição
 */
const DoughnutChart = ({ data, dataKey = 'total', nameKey = 'status', title, subtitle }) => {
    
    // Verifica se os dados existem e são um array válido antes de processar
    if (!data || !Array.isArray(data) || data.length === 0) {
        return (
            <CardContent className="p-6">
                <div className="mb-4">
                    <h3 className="text-lg font-semibold mb-2">{title}</h3>
                    <p className="text-gray-500 text-sm">{subtitle}</p>
                </div>
                <div className="h-64 flex items-center justify-center">
                    <p className="text-gray-500">Sem dados para exibir.</p>
                </div>
            </CardContent>
        );
    }
    
    // Mapeia os dados de entrada para o formato que o Recharts espera,
    // garantindo que as chaves sejam 'name' e 'value'
    const chartData = data
        .map((item) => ({
            name: item[nameKey] || 'Sem nome',
            value: Number(item[dataKey]) || 0
        }))
        .filter(item => item.value > 0); // Remove itens com valor zero ou inválido

    // Função para renderizar os rótulos de forma personalizada no centro
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
        // Exibe apenas a porcentagem para um visual mais limpo no Rosca
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
        const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));

        return (
            <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    if (!chartData || chartData.length === 0) {
        return (
            <CardContent className="p-6">
                <div className="mb-4">
                    <h3 className="text-lg font-semibold mb-2">{title}</h3>
                    <p className="text-gray-500 text-sm">{subtitle}</p>
                </div>
                <div className="h-64 flex items-center justify-center">
                    <p className="text-gray-500">Sem dados para exibir.</p>
                </div>
            </CardContent>
        );
    }

    return (
        <CardContent className="p-6">
            <div className="mb-4">
                <h3 className="text-base sm:text-lg font-semibold mb-2">{title}</h3>
                <p className="text-gray-500 text-xs sm:text-sm">{subtitle}</p>
            </div>
            <div className="h-64 sm:h-72 md:h-80" role="img" aria-label="Gráfico de pizza">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 10, right: 10, bottom: 60, left: 10 }}>
                        <Pie
                            data={chartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="45%"
                            innerRadius="35%"
                            outerRadius="55%"
                            fill="#8884d8"
                            paddingAngle={5}
                            labelLine={false}
                            // label={renderCustomizedLabel} // Rótulos internos
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip 
                            formatter={(value, name) => [`${value} itens`, name]}
                        />
                        <Legend 
                            verticalAlign="bottom"
                            align="center"
                            height={50}
                            wrapperStyle={{ 
                                fontSize: '13px',
                                paddingTop: '8px',
                                paddingBottom: '8px'
                            }}
                            iconSize={12}
                            iconType="circle"
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </CardContent>
    );
};

export default DoughnutChart;
